/**
 * Colour of the therapist row's assigned-time figure and its clock tick.
 *
 * Extracted verbatim from therapist-infoV3.tsx so the APIT-667 decision below is unit-testable
 * without mounting a 2,500-line MUI/Redux component. `isProductiveTime` is unchanged.
 */

/** Nothing to say: the grey the available-time figure, the `/` separator and both clock icons use. */
export const NEUTRAL_COLOR = '#757B87';
/** Met the productivity goal without exceeding available hours. */
export const ON_TARGET_COLOR = '#009A00';
/** Assigned more than the therapist has available. Does not involve the productivity target. */
export const OVER_AVAILABLE_COLOR = '#B42318';

export function isProductiveTime(actualHrs: number, availableHours: number, productivity: number) {
  const effectiveHours = availableHours * (productivity / 100);
  if (actualHrs === 0) return NEUTRAL_COLOR;

  return actualHrs >= effectiveHours && actualHrs <= availableHours
    ? ON_TARGET_COLOR
    : actualHrs > availableHours
    ? OVER_AVAILABLE_COLOR
    : NEUTRAL_COLOR;
}

/**
 * APIT-667. When an enterprise switches productivity targets off, `targetProductivity` is NOT
 * cleared server-side — TherapistSchedulingServiceImplV2 overrides only the PTIF value and
 * ProviderRepo selects the stored target raw. So the row would otherwise still be coloured
 * against a target the enterprise just turned off: a stale stored goal keeps grading the
 * therapist, and a null goal makes `null / 100` zero, which passes the on-target comparison and
 * paints every therapist with any assignment green.
 *
 * With targets off the row therefore renders the way this component already renders a therapist
 * who has no target: neutral grey, except that assigned-over-available stays red. That branch
 * compares assigned hours to available hours and never reads `productivity`, so it means exactly
 * the same thing whether targets are on or off — dropping it would remove a real over-booking
 * warning rather than neutralise a productivity verdict.
 *
 * With targets on this is `isProductiveTime` and nothing else, so the other tenants are untouched.
 */
export function assignedTimeColor(
  actualHrs: number,
  availableHours: number,
  productivity: number,
  productivityTargetsEnabled: boolean
) {
  if (productivityTargetsEnabled) {
    return isProductiveTime(actualHrs, availableHours, productivity);
  }
  return actualHrs > availableHours ? OVER_AVAILABLE_COLOR : NEUTRAL_COLOR;
}
